import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { INPUT_ERROR_MESSAGE, USER_BENEFICIARY } from '../constants/stolt-constant';
import { Platform } from '@ionic/angular';
import { AppConfig } from 'src/app/app.config';
import { environment } from 'src/environments/environment';
import { AlertController } from '@ionic/angular';

@Injectable({
  providedIn: 'root'
})
export class CrewSelfService {

  private readonly subject = new Subject<any>();


  private readonly isDesktop$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly pageRefresh$: BehaviorSubject<any> = new BehaviorSubject({ page: '', refresh: false });
  private readonly currentPlatform$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private readonly fillipino$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly poolCode$: BehaviorSubject<any> = new BehaviorSubject(0);
  private readonly modelSuccess$: BehaviorSubject<any> = new BehaviorSubject({ title: '', success: false,dismiss:false });
  private readonly activeUser$: BehaviorSubject<any> = new BehaviorSubject(null);
  private readonly basicNotification$: BehaviorSubject<any> = new BehaviorSubject(null);
  private readonly highSchoolAllowanceNotification$: BehaviorSubject<any> = new BehaviorSubject(false);
  private readonly loanAddDisabled$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private readonly loanRestrictionMessage$: BehaviorSubject<string> = new BehaviorSubject('');
  /** Payment from rules for Add loan form (from GET /loans/verify-bassnet-activity). */
  private readonly loanPaymentFromRules$: BehaviorSubject<any> = new BehaviorSubject(null);

  constructor(
    private readonly platform: Platform,
    private readonly alertController: AlertController,
  ) { }

  public getEncyptionKey(forConfig=false){
    const keys = {
      key: (forConfig ? environment.CREW_APP_ENCRYPTION_KEY : AppConfig.settings?.INTRANSIT_ENCRYPTION_KEY),
      iv: (forConfig ? environment.CREW_APP_ENCRYPTION_IV : AppConfig.settings?.INTRANSIT_ENCRYPTION_IV),
    };
    // Constants for encryption and decryption
    const encryptionKey = CryptoJS.enc.Utf8.parse(keys.key); // 32 characters (256 bits)
    const encryptionIV = CryptoJS.enc.Utf8.parse(keys.iv); // 16 characters (128 bits)
    return {encryptionKey, encryptionIV};
  }

  public encryptData(data: any) {
    const {encryptionKey, encryptionIV} = this.getEncyptionKey();
    const dataString = JSON.stringify(data);
    const encrypted = CryptoJS.AES.encrypt(
      dataString, 
      encryptionKey, 
      { iv: encryptionIV, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );
    return encrypted.toString();
  }

  public decryptData(encryptedData: any, switchKey: boolean=false) {
    const {encryptionKey, encryptionIV} = this.getEncyptionKey(switchKey);
    const decrypted = CryptoJS.AES.decrypt(
      encryptedData, 
      encryptionKey, 
      { iv: encryptionIV, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedString);
  }

  public encryptHttpRequestBody(req: any) {
    const isUploadFileRequest = req.url.includes('/upload-file');    
    if (!isUploadFileRequest && req.body) {
      const encryptedReqBody = this.encryptData(req.body);  // Encrypt the body
      req = req.clone({
        body: { payload: encryptedReqBody }
      });
    }
    return req;
  }

  public getChangedFields(initialValue: any, currentValue: any): string[] {
    const changedFields: string[] = [];
    Object.keys(initialValue).forEach((key) => {
      const originalValue = this.normalizeFieldValue(initialValue[key]);
      const currentValueField = this.normalizeFieldValue(currentValue[key]);
      if (Array.isArray(originalValue) && Array.isArray(currentValueField)) {
        if (originalValue.length !== currentValueField.length ||
            !originalValue.every((value, index) => value === currentValueField[index])) {
          changedFields.push(key);
        }
      } else if (originalValue !== currentValueField) {
        changedFields.push(key);
      }
    });
    return changedFields;
  }

  public getChangedFieldsArray(initialValue: any, currentValue: any): string[] {
    const changedFields: string[] = [];
  
    // Handle arrays
    if (Array.isArray(initialValue) && Array.isArray(currentValue)) {
      const maxLength = Math.max(initialValue.length, currentValue.length);
  
      for (let i = 0; i < maxLength; i++) {
        const initItem = initialValue[i];
        const currItem = currentValue[i];
  
        if (!initItem || !currItem) {
          changedFields.push(`index_${i}`);
          continue;
        }
  
        // Compare fields of each object
        Object.keys(initItem).forEach((key) => {
          const originalValue = this.normalizeFieldValue(initItem[key]);
          const currentValueField = this.normalizeFieldValue(currItem[key]);
  
          if (Array.isArray(originalValue) && Array.isArray(currentValueField)) {
            if (
              originalValue.length !== currentValueField.length ||
              !originalValue.every((val, idx) => val === currentValueField[idx])
            ) {
              changedFields.push(`index_${i}.${key}`);
            }
          } else if (originalValue !== currentValueField) {
            changedFields.push(`index_${i}.${key}`);
          }
        });
      }
  
      return changedFields;
    }
  
    // Handle objects (fallback to original logic)
    if (initialValue && currentValue && typeof initialValue === 'object' && typeof currentValue === 'object') {
      Object.keys(initialValue).forEach((key) => {
        const originalValue = this.normalizeFieldValue(initialValue[key]);
        const currentValueField = this.normalizeFieldValue(currentValue[key]);
  
        if (Array.isArray(originalValue) && Array.isArray(currentValueField)) {
          if (
            originalValue.length !== currentValueField.length ||
            !originalValue.every((value, index) => value === currentValueField[index])
          ) {
            changedFields.push(key);
          }
        } else if (originalValue !== currentValueField) {
          changedFields.push(key);
        }
      });
  
      return changedFields;
    }
  
    // Fallback for non-object values
    if (this.normalizeFieldValue(initialValue) !== this.normalizeFieldValue(currentValue)) {
      changedFields.push('value');
    }
  
    return changedFields;
  }
  

  public getChangedFieldsIgnore(initialValue: any, currentValue: any): string[] {
    const changedFields: string[] = [];
    const ignoredFields = ['AMOUNT_IN_USD']; // Add any fields to ignore here
  
    Object.keys(initialValue).forEach((key) => {
      if (ignoredFields.includes(key)) {
        return; // Skip this field
      }
  
      const originalValue = this.normalizeFieldValue(initialValue[key]);
      const currentValueField = this.normalizeFieldValue(currentValue[key]);
  
      if (Array.isArray(originalValue) && Array.isArray(currentValueField)) {
        if (
          originalValue.length !== currentValueField.length ||
          !originalValue.every((value, index) => value === currentValueField[index])
        ) {
          changedFields.push(key);
        }
      } else if (originalValue !== currentValueField) {
        changedFields.push(key);
      }
    });
  
    return changedFields;
  }

  gethospitalChangedFields(initial: any, current: any): string[] {
    const changedFields: string[] = [];
    for (const key in current) {
      const val1 = initial[key];
      const val2 = current[key];
      
      // Simple deep compare
      const areEqual = JSON.stringify(val1) === JSON.stringify(val2);
      if (!areEqual) {
        changedFields.push(key);
      }
    }
    return changedFields;
  }
  

  public normalizeFieldValue(value: any): any {
    if (typeof value === 'string') {
      return value.toLowerCase().trim();
    }
    return value;
  }

  sendClickEvent(event: any) {
    this.subject.next(event); // Triggering an event
  }



  getClickEvent(): Observable<any> {
    return this.subject.asObservable();
  }

  isDesktopSet() {
    this.platform.ready().then(() => {
      if (this.platform.width() < 768) {
        this.isDesktop$.next(false);
      }
      else {
        this.isDesktop$.next(true);
      }
    });
  }

  

  isDesktopGet() {
    return this.isDesktop$.asObservable();
  }

  pageRefreshSet(page: string, refresh: boolean) {
    this.pageRefresh$.next({ page, refresh });
  }

  setTab(page: string,tab:string,id:string = '') {
    this.pageRefresh$.next({ page,tab,id});
  }

  getTab() {
    return this.pageRefresh$.asObservable();
  }

  pageRefreshGet() {
    return this.pageRefresh$.asObservable();
  }


  modalSuccessSet(title:string,success:boolean,dismiss:boolean = false) {
    this.modelSuccess$.next({ title, success, dismiss });
  }

  modalSuccessGet() {
    return this.modelSuccess$.asObservable();
  }

  isFillipinoSet(userType:any) {
    let fillipino = USER_BENEFICIARY.FILIPINO_POOL_CODE.includes(parseInt(userType));
    this.fillipino$.next(fillipino);
  }

  isFillipinoGet() {
    return this.fillipino$.asObservable();
  }

  isBasicNotificationSet(basicNotification:any) {
    this.basicNotification$.next(basicNotification);
  }

  isBasicNotificationGet() {
    return this.basicNotification$.asObservable();
  }

  isPoolCodeSet(userType:any) {
    this.poolCode$.next(userType);
  }

  isPoolCodeGet() {
    return this.poolCode$.asObservable();
  }

  isSetActiveUser(user:any) {
    this.activeUser$.next(user);
  }

  isActiveUserGet() {
    return this.activeUser$.asObservable();
  }

  setCurrentPlatform() {
    if (this.platform.is('ios') || this.platform.is('android') && !( this.platform.is('desktop') || this.platform.is('mobileweb') ) ) {
      this.currentPlatform$.next('mobile');
    } else {
      this.currentPlatform$.next('browser');
    }
  }

  getCurrentPlatform() {
    return this.currentPlatform$.asObservable();
  }

  dropdownOptions(data: any, id: string, name: string, toUpperCase = false) {
    let options: { id: any; name: any; }[] = [];
    data.forEach((element: any) => {
      if (element[id] && element[name]) {
        let labelName  = (toUpperCase) ? element[name].toUpperCase() : element[name];
        options.push({ id: element[id], name: labelName });
      }

    });
    return options;
  }

  dropdownFormTypesOptions(data: any, id: string, name: string, toUpperCase = false) {
    let options: { id: any; name: any; }[] = [];
    data.forEach((element: any) => {
      if (element?.FORM_TYPE) {
        let labelName  = (toUpperCase) ? element.FORM_TYPE.toUpperCase() : element.FORM_TYPE;
        options.push({ id: element.FORM_TYPE, name: labelName });
      }
    });
   
    return options;
  }


  public showInputError(field: string, type: string, data: any,minlength:number = 0) {
    if (data.get(field)!.invalid && data.get(field)!.touched && data.get(field)!.hasError('pattern')) {
      return this.errorMsg(field, type);
    }
    else if (data.get(field)!.invalid && data.get(field)!.touched && data.get(field)!.hasError('maxlength')) {
      return this.errorMsg(field, 'maxlength');
    }
    else if (data.get(field)!.invalid && data.get(field)!.touched && data.get(field)!.hasError('max') && type !== 'quantity-max') {
      return this.errorMsg(field, 'max');
    }
    else if (data.get(field)!.invalid && data.get(field)!.hasError('min') && field == 'PERCENTAGE') {
      return this.errorMsg(field, 'min');
    }
    else if (data.get(field)!.invalid && data.get(field)!.touched && data.get(field)!.hasError('minlength')) {
      return this.errorMsg(field, 'minlength',minlength);
    }
    else if(data.get(field)!.invalid && data.get(field)!.touched && type === "quantity-max"){
      return this.errorMsg(field, 'quantity-max');
    }
    return '';
  }
  public showOnLoadInputError(field: string, type: string, data: any,minlength:number = 0) {
    if (data.get(field)!.invalid  && data.get(field)!.hasError('pattern')) {
      return this.errorMsg(field, type);
    }
    else if (data.get(field)!.invalid && data.get(field)!.hasError('maxlength')) {
      return this.errorMsg(field, 'maxlength');
    }
    else if (data.get(field)!.invalid && data.get(field)!.hasError('max') && type !== 'quantity-max') {
      return this.errorMsg(field, 'max');
    }
    else if (data.get(field)!.invalid && data.get(field)!.hasError('min') && field == 'PERCENTAGE') {
      return this.errorMsg(field, 'min');
    }
    else if (data.get(field)!.invalid && data.get(field)!.hasError('minlength')) {
      return this.errorMsg(field, 'minlength',minlength);
    }
    else if(data.get(field)!.invalid && type === "quantity-max"){
      return this.errorMsg(field, 'quantity-max');
    }
    return '';
  }

  public formatFamilyMemberName(firstName: any, lastName: any): string {
    const first = this.sanitizeDisplayValue(firstName);
    const last = this.sanitizeDisplayValue(lastName);
    return `${first} ${last}`.trim();
  }

  private sanitizeDisplayValue(value: any): string {
    return value != null && value !== '' && value !== 'NULL' ? String(value) : '';
  }

  errorMsg(field: string, type: string,minlength:number = 0) {
    if (type == 'string') {
      return INPUT_ERROR_MESSAGE.CHARACTOR_ONLY;
    }
    else if (type == 'number') {
      return INPUT_ERROR_MESSAGE.NUMBER_ONLY;
    }
    else if (type == 'special') {
      return INPUT_ERROR_MESSAGE.SPECIAL;
    }
    else if (type == 'numeric-special-allowed') {
      return INPUT_ERROR_MESSAGE.NUMERIC_SPECIAL_ALLOWED;
    }
    else if (type == 'maxlength') {
      return INPUT_ERROR_MESSAGE.MAXLENGTH;
    }
    else if (type == 'minlength' && field == 'ACCOUNT_CODE') {
      return INPUT_ERROR_MESSAGE.MINLENGTH_ACCOUNT_CODE.replace('number', minlength.toString());
    }
    else if (type == 'max') {
      return INPUT_ERROR_MESSAGE.PERCENTAGE_MAX;
    }
    else if (type == 'min') {
      return INPUT_ERROR_MESSAGE.PERCENTAGE_MAX;
    }
    else if (type == 'percentage') {
      return INPUT_ERROR_MESSAGE.PERCENTAGE_DATA_FORMET;
    }
    else if (type == 'decimal') {
      return INPUT_ERROR_MESSAGE.DECIMAL_DATA_FORMET;
    }
    else if (type == '4-on-2-decimal') {
      return INPUT_ERROR_MESSAGE.DECIMAL_DATA_FORMET_FOUR_NO;
    }
    else if (type == 'quantity-max') {
      return INPUT_ERROR_MESSAGE.QUANTITY_MAX;
    }
    else if (type == 'numericOnly') {
      return INPUT_ERROR_MESSAGE.NUMERIC_ONLY;
    }
    else if (type == 'harmful-characters-not-allowed') {
      return INPUT_ERROR_MESSAGE.HARMFUL_CHARACTERS_NOT_ALLOWED;
    }
    else if (type == 'numeric-special-allowed-except-harmful') {
      return INPUT_ERROR_MESSAGE.NUMERIC_SPECIAL_ALLOWED_EXCEPT_HARMFUL;
    }
    else {
      return '';
    }

  }

  compareObjects(original: any, updated: any): boolean {
    return JSON.stringify(original) === JSON.stringify(updated);
  }

  hasDataChanged(original: any, updated: any): boolean {
    return JSON.stringify(original) !== JSON.stringify(updated);
  }

  formatDescription(description: string): string {
    if (description && description !== 'NULL') {
      return description.replace(/\n/g, '<br>');
    }
    return '-';
  }

  showMaintenanceAlert(): Promise<boolean> {
    if(AppConfig.settings?.MAINTENANCE_ALERT_ON) return new Promise(() => {});
    return new Promise(async (resolve) => {
      AppConfig.settings.MAINTENANCE_ALERT_ON = true;
      const alert = await this.alertController.create({
        header: AppConfig.settings?.MAINTENANCE_MODE_TITLE || "We'll be back soon!",
        message: AppConfig.settings?.MAINTENANCE_MODE_MESSAGE || "Service is temporarily unavailable. Please try again later.",
        backdropDismiss: false,
        buttons: [
          {
            text: 'Retry',
            role: 'reload',
            handler: async () => {
              location.reload();
            }
          }
        ]
      });

      await alert.present();
    });
  }  

  highSchoolAllowanceNotificationSet(notification:boolean) {
    this.highSchoolAllowanceNotification$.next(notification);
  }

  highSchoolAllowanceNotificationGet() {
    return this.highSchoolAllowanceNotification$.asObservable();
  }

  loanAddDisabledSet(disabled: boolean) {
    this.loanAddDisabled$.next(!!disabled);
  }

  loanAddDisabledGet() {
    return this.loanAddDisabled$.asObservable();
  }

  loanRestrictionMessageSet(message: string) {
    this.loanRestrictionMessage$.next(message || '');
  }

  loanRestrictionMessageGet() {
    return this.loanRestrictionMessage$.asObservable();
  }

  loanPaymentFromRulesSet(rules: any) {
    this.loanPaymentFromRules$.next(rules || null);
  }

  loanPaymentFromRulesGet() {
    return this.loanPaymentFromRules$.asObservable();
  }

  /**************************** --------------END------END-----------  ************************************/
}